import React, { useRef, useState } from 'react';
import { Alert, Button } from 'antd';
import styles from './index.module.css';
import track from 'utils/analytics';
import { HarvestIntegration } from 'appJS/components/Integrations/types';
import Hint from 'components/Hint';
import { IntegrationItem } from './integrationItem';
import { Image } from 'antd';
import {
  integrationEntriesForKind,
  integrationsInfo
} from './integrationsInfo';
import {
  INTEGRATION_CATEGORY_TABS,
  getInitialIntegrationCategory,
  IntegrationCategoryFilter
} from './categoryTabs';
import categoryTabStyles from './categoryTabs.module.css';

const normalizeMcpClientName = (clientName?: string): string =>
  clientName
    ?.toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, '') || '';

export interface IntegrationCatalogProps {
  project: {
    organizationId: string;
    id: string;
  };
  zapier: {
    id: string;
  };
  legacyIntegrations: object;
  premiumIntegrationsAllowed: boolean;
  editingAllowed: boolean;
  bugherdUrl: string;
  ownerEmail: string;
  harvestIntegration: HarvestIntegration;
  trialUser: boolean;
  switchModal: Function;
  mcpConnectedClientNames?: string[];
  connectors: object[];
  currentUser: {
    id: string;
    name: string;
    email: string;
    viewedGuestProjectBoard: boolean;
    role: string;
  };
}

const IntegrationCatalog: React.FC<IntegrationCatalogProps> = ({
  premiumIntegrationsAllowed,
  project,
  legacyIntegrations: _legacyIntegrations = {},
  switchModal,
  zapier,
  connectors = [],
  harvestIntegration,
  ownerEmail,
  bugherdUrl,
  trialUser,
  editingAllowed,
  currentUser,
  mcpConnectedClientNames
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const integrationsWithModals = [
    'webhooks',
    'harvest',
    'teams',
    'teamwork',
    'trellonative',
    'jiranative',
    'github',
    'slack',
    'asananative',
    'clickupnative',
    'linearnative',
    'mondaynative',
    'productivenative'
  ];
  const [showUpgrade, setShowUpgrade] = useState<boolean>(false);
  const [popOverToShow, setPopOverToShow] = useState<string>('');
  const [showPreview, setShowPreview] = useState<string>('');
  const [activeCategory, setActiveCategory] = useState<
    IntegrationCategoryFilter
  >(getInitialIntegrationCategory);

  const handleCategoryChange = (value: IntegrationCategoryFilter) => {
    setActiveCategory(value);
    const categoryLabel =
      INTEGRATION_CATEGORY_TABS.find(tab => tab.value === value)?.label ??
      value;
    track('Integrations filter clicked', {
      category: categoryLabel.replace('/', '-')
    });
    const params = new URLSearchParams(window.location.search);
    if (value === 'all') {
      params.delete('category');
    } else {
      params.set('category', value);
    }
    const search = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}`
    );
  };
  const matchingConnectorFor = (integrationName: string) => {
    return connectors.find((c: any) => {
      const code = c.code.toLowerCase();
      const checkName = integrationName.toLowerCase().replace('native', '');
      return (
        code === checkName ||
        code === checkName.replace(/\./g, '') ||
        (integrationName === 'teams' && code === 'team')
      );
    });
  };

  const showIntegration = async (integration: string) => {
    const legacyIntegrations: any = _legacyIntegrations;

    if (integrationsWithModals.includes(integration)) {
      // TODO: Add thing here to tell the model to do something
      switchModal(integration);
      return;
    } else if (
      legacyIntegrations[integration] &&
      legacyIntegrations[integration].enabled
    ) {
      window.location.pathname = `organizations/${project?.organizationId}/integrations/${legacyIntegrations[integration].id}/edit`;
    } else if (integration === 'zapier' && zapier.id) {
      window.location.pathname = `settings/organizations/${project?.organizationId}/integrations/${zapier.id}/edit`;
    } else {
      window.location.pathname = `organizations/${project?.organizationId}/integrations/new/${integration}`;
    }
  };

  interface IntegrationStatus {
    id: string | undefined;
    enabled: boolean;
    loaded?: boolean;
  }

  type IntegrationList = Record<string, IntegrationStatus>;

  // TODO(integrations_cleanup): this also shouldn't exist. Hard coded lists bring complexity and difficulty in maintaining.
  const integrations = (): IntegrationList => {
    const connectedMcpClientNamesSet = new Set(
      (mcpConnectedClientNames || [])
        .map(normalizeMcpClientName)
        .filter(Boolean)
    );

    const integrationList: IntegrationList = {
      basecamp: {
        id: _legacyIntegrations.basecamp?.id,
        enabled: !!_legacyIntegrations.basecamp
      },
      github: {
        id: _legacyIntegrations.github?.id,
        enabled: !!_legacyIntegrations.github
      },
      slack_legacy: {
        id: _legacyIntegrations.slack_legacy?.id,
        enabled: !!_legacyIntegrations.slack_legacy
      },
      zapier: {
        id: _legacyIntegrations.zapier?.id,
        enabled: !!_legacyIntegrations.zapier
      },
      webhooks: { id: 'webhooks', enabled: true },
      teams: { id: 'teams', enabled: !!_legacyIntegrations.team },
      teamwork: { id: 'teamwork', enabled: !!_legacyIntegrations.teamwork },
      trellonative: {
        id: _legacyIntegrations.trello?.id,
        enabled: !!_legacyIntegrations.trello,
        loaded: false
      }
    };

    integrationEntriesForKind('mcp').forEach(
      ([integrationKey, integrationInfo]) => {
        const normalizedLabel = normalizeMcpClientName(
          integrationInfo.label || integrationKey
        );

        integrationList[integrationKey] = {
          id: integrationKey,
          enabled: connectedMcpClientNamesSet.has(normalizedLabel)
        };
      }
    );

    const activeConnectors = new Set(
      connectors.map((c: any) => c.code.toLowerCase())
    );

    [
      'harvest',
      'jira',
      'asana',
      'linear',
      'clickup',
      'monday',
      'slack',
      'productive'
    ].forEach(integration => {
      if (!activeConnectors.has(integration)) return;
      const isNative = [
        'jira',
        'asana',
        'linear',
        'clickup',
        'monday',
        'productive'
      ].includes(integration);
      const legacyIntegration = _legacyIntegrations[integration];
      if (integration === 'slack') {
        integrationList[integration] = {
          id: integration,
          enabled: !!legacyIntegration
        };
        delete integrationList.slack_legacy;
      } else {
        const status: IntegrationStatus = {
          id: legacyIntegration?.id,
          enabled: !!legacyIntegration
        };
        if (integration !== 'harvest') status.loaded = false;
        integrationList[
          isNative ? `${integration}native` : integration
        ] = status;
        if (isNative) {
          delete integrationList[integration];
        }
      }
    });

    return integrationList;
  };

  const integrationList = integrations();

  return (
    <div>
      {showPreview && (
        <Image
          src={showPreview}
          style={{ display: 'none' }}
          preview={{
            src: showPreview,
            getContainer: () =>
              document.getElementById('settingsBoard') as HTMLDivElement,
            visible: !!showPreview,
            onVisibleChange: visible => {
              if (!visible) setShowPreview('');
            },
            toolbarRender: (_, info) => null,
            imageRender: () => {
              const isYoutube = showPreview.includes('youtu.be');
              const isVimeo = showPreview.includes('vimeo');
              const url = new URL(showPreview);
              const id = url.pathname.split('/').pop();
              url.search = '';
              if (!isVimeo) url.pathname = `/embed/${id}`;
              if (isYoutube) url.hostname = 'www.youtube.com';
              return (
                <iframe
                  src={url.href}
                  style={{ border: 'none' }}
                  width="90%"
                  height={isYoutube ? '80%' : '68%'}
                  allowFullScreen
                />
              );
            }
          }}
        />
      )}
      <div ref={modalRef} className={styles.container}>
        {!editingAllowed && (
          <Alert
            showIcon
            type="info"
            className={styles.alert}
            message="Some integrations require “Manager” permissions to configure."
          />
        )}
        <div className={categoryTabStyles.categoryTabs}>
          {INTEGRATION_CATEGORY_TABS.map(tab => (
            <Button
              key={tab.value}
              shape="round"
              type={activeCategory === tab.value ? 'primary' : 'default'}
              className={
                activeCategory === tab.value
                  ? categoryTabStyles.categoryTabActive
                  : categoryTabStyles.categoryTab
              }
              onClick={() => handleCategoryChange(tab.value)}
            >
              {tab.label}
            </Button>
          ))}
        </div>
        <ul>
          {Object.keys(integrationList)
            .filter(name => {
              if (activeCategory === 'all') return true;
              const info = integrationsInfo[name.replace(/\./g, '')];
              return info?.category === activeCategory;
            })
            .filter(name => {
              const info = integrationsInfo[name.replace(/\./g, '')];
              if (
                info?.kind === 'mcp' ||
                name === 'webhooks' ||
                name === 'trellonative'
              )
                return true;

              if (integrationList[name]?.enabled) return true;

              return !!matchingConnectorFor(name);
            })
            .sort((a, b) => {
              const labelA = (
                integrationsInfo[a.replace(/\./g, '')]?.label ?? a
              ).toLowerCase();
              const labelB = (
                integrationsInfo[b.replace(/\./g, '')]?.label ?? b
              ).toLowerCase();
              return labelA.localeCompare(labelB);
            })
            .map((integrationName, index) => {
              const matchingConnector = matchingConnectorFor(integrationName);

              return (
                <IntegrationItem
                  key={integrationName}
                  name={integrationName}
                  isBeta={(matchingConnector as any)?.is_beta}
                  premiumIntegrationsAllowed={premiumIntegrationsAllowed}
                  isTrialUser={trialUser}
                  integrationList={integrationList}
                  handleShowIntegration={showIntegration}
                  handleShowUpgrade={() => {
                    setShowUpgrade(!showUpgrade);
                    setPopOverToShow(integrationName);
                  }}
                  // @ts-expect-error
                  role={currentUser.role}
                  editingAllowed={editingAllowed}
                  setPopOverToShow={setPopOverToShow}
                  popOverToShow={popOverToShow}
                  ownerEmail={ownerEmail}
                  bugherdUrl={bugherdUrl}
                  organizationId={project?.organizationId ?? ''}
                  setShowPreview={setShowPreview}
                  index={index}
                />
              );
            })}
        </ul>
      </div>
      {currentUser && (
        <Hint
          message={
            <div className={styles.suggestIntegrationTooltip}>
              <span>What are we missing?</span>
              <a
                href={`https://bugherd.typeform.com/to/e9bhE6vO#ref=${project?.organizationId}:${currentUser.id}`}
                target="_blank"
              >
                Suggest an integration
              </a>
            </div>
          }
        />
      )}
    </div>
  );
};

export default (props: IntegrationCatalogProps) => (
  <IntegrationCatalog {...props} />
);
